/**
 * Which equipment may be returned to a yard.
 *
 * This mirrors a SERVER rule, and mirroring it is the whole point — the same
 * reason `lib/transfer-destination.ts` exists next door.
 * `POST /equipment/:id/return` accepts exactly these two statuses, in both its
 * pre-read and its UPDATE:
 *
 *     AND status IN ('left_on_site', 'in_use')
 *
 * and says why in its own docblock: "since job completion stopped
 * auto-releasing in_use gear, this is the endpoint a tech uses to say 'I
 * brought it back', and refusing in_use would leave that gear with no way home
 * short of an admin edit."
 *
 * ─── WHAT THIS FIXES, AND IT WAS TWO DIFFERENT WRONG ANSWERS ────────────────
 *
 * Three surfaces spoke to one endpoint and none of them agreed with it:
 *
 *   the API           in_use, left_on_site
 *   the FIELD screen  left_on_site, or a completed job  (`canDispose`)
 *   the OFFICE screen left_on_site only
 *
 * The field rule was the one Derek reported from TNT: a hand cannot say he
 * brought gear back until an office user closes the job, so equipment
 * physically sitting in the shop reads `in_use` for as long as the paperwork
 * takes. The screen's own comment stated the premise it was built on — "while
 * the job is running the hand still has it and has not brought it anywhere" —
 * and that premise is just false. Gear comes off a running job and goes home
 * mid-job; that is ordinary, not an exception. It is the same false premise
 * that tied Transfer to Left on Site until `canTransfer` was widened, and
 * Return was simply left behind at that time.
 *
 * The office rule was narrower still, and narrow in a way nobody had noticed
 * because the field screen was the one being looked at.
 *
 * THE JOB'S STATUS IS NOT PART OF THIS GATE, and dropping it is deliberate
 * rather than an omission. The server does not consult the job at all — only
 * the equipment's own status — so any job-status term here could only ever
 * make the two disagree. It is also unnecessary: on a COMPLETED job,
 * `GET /employee/my-equipment` can only surface a row through arm 2 or arm 3,
 * and both of those already pin the status to `left_on_site` or `in_use`
 * respectively. So the old `jobStatus === 'completed'` term never widened the
 * gate past what this function returns — it only failed to cover the live-job
 * case, which is the bug.
 *
 * The mirror image matters too: `available` and `reserved` are excluded here
 * exactly as the server excludes them. Arm 1 of that endpoint lists gear on a
 * live job at ANY status, so a reserved asset does reach this screen, and
 * offering Return on it would have earned a 404 and a red toast with no way
 * for the hand to tell which gear was eligible. A picker wider than the rule
 * is as broken as one narrower.
 */

/** Equipment statuses `POST /equipment/:id/return` accepts. */
export const RETURNABLE_STATUSES = ['in_use', 'left_on_site'] as const;

/**
 * May this equipment be returned to a yard?
 *
 * Takes the status alone rather than the record, because the two screens hand
 * it two different record shapes — the field rows are raw snake_case from
 * `GET /employee/my-equipment`, the office rows are camelCase `Equipment` —
 * and the status is the only field either of them needs to answer this.
 */
export function canReturnToYard(status: string | null | undefined): boolean {
  return (RETURNABLE_STATUSES as readonly string[]).includes(status ?? '');
}
